<script lang="ts">
	import { tick, untrack } from 'svelte';
	import { goto } from '$app/navigation';
	import { toast } from 'svelte-sonner';
	import * as Dialog from '$lib/components/ui/dialog/index.js';
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js';
	import { useSidebar } from '$lib/components/ui/sidebar/index.js';
	import SearchIcon from '@lucide/svelte/icons/search';
	import XIcon from '@lucide/svelte/icons/x';
	import ChevronDownIcon from '@lucide/svelte/icons/chevron-down';
	import ArrowLeftIcon from '@lucide/svelte/icons/arrow-left';
	import CornerDownLeftIcon from '@lucide/svelte/icons/corner-down-left';
	import EyeIcon from '@lucide/svelte/icons/eye';
	import MessageCircleIcon from '@lucide/svelte/icons/message-circle';
	import ExternalLinkIcon from '@lucide/svelte/icons/external-link';
	import LinkIcon from '@lucide/svelte/icons/link';
	import Spinner from './Spinner.svelte';
	import { getSessions } from '$lib/sessions-store.svelte';
	import { isTemporary } from '$lib/temp-chats.svelte';
	import { loadHistory, searchSessions, SearchUnavailableError, type SessionSearchResult } from '$lib/api';
	import { foldHistory, type FeedMessage } from '$lib/history-fold';
	import {
		firstHitIndex,
		highlightSegments,
		previewRows,
		queryTerms,
		recentItems,
		resultItems,
		searchLocally,
		stepSelection,
	} from '$lib/chat-search';

	// ⌘K chat search, after the Ollama desktop search window: query bar on top
	// (clear ×, scope "All ▾", close ×), "Results N" list on the left (chat
	// icon, title, "Personal chat", one excerpt with a yellow highlight), the
	// selected chat on the right ("← Back … Chats", title, user bubbles right /
	// assistant text left with timestamps), and a footer ("Personal chat" ·
	// Open ↵ · Preview · Actions ⌘K). ↑/↓ moves, ↵ opens, esc closes. With an
	// empty query the list shows the recent chats.
	//
	// Matching is the manager's GET /sessions/search (titles + user messages +
	// assistant replies of the caller's own chats). When the manager predates
	// that route (404 — frontend and manager deploy independently) the dialog
	// searches in the browser instead: the most recent chats' transcripts are
	// pulled through the existing per-chat /messages endpoint and matched with
	// the same rules (chat-search.ts searchLocally), so the search box never
	// goes dead. Transcripts are cached for the dialog's lifetime and shared
	// with the preview pane.

	let { open = $bindable(false) }: { open?: boolean } = $props();

	const sessions = getSessions();
	// Rendered inside Sidebar.Provider, so the mobile drawer can be closed when
	// a result opens a chat (otherwise the sheet would stay over the new chat).
	const sidebar = useSidebar();

	let query = $state('');
	let inputEl = $state<HTMLInputElement | undefined>(undefined);
	let listEl = $state<HTMLElement | undefined>(undefined);
	let previewEl = $state<HTMLElement | undefined>(undefined);

	// Server (or browser-side fallback) search state. `results === null` ⇒ no
	// query, or the first answer is still pending.
	let results = $state.raw<SessionSearchResult[] | null>(null);
	let searching = $state(false);
	let searchError = $state<string | null>(null);
	// Flipped the first time the manager answers 404 — stays on for the dialog
	// (one 404 is enough to know this manager has no search route).
	let localMode = $state(false);
	let localSkipped = $state(0);

	type Scope = 'all' | 'titles' | 'messages';
	let scope = $state<Scope>('all');
	const SCOPE_LABEL: Record<Scope, string> = { all: 'All', titles: 'Titles', messages: 'Messages' };

	const terms = $derived(queryTerms(query));
	const scopedResults = $derived(
		(results ?? []).filter((r) => (scope === 'all' ? true : scope === 'titles' ? r.titleMatch : r.matchCount > 0)),
	);
	// Temporary chats are never listed (SAY-1820).
	const items = $derived(terms.length === 0 ? recentItems(sessions, undefined, isTemporary) : resultItems(scopedResults));
	// Re-anchor the selection only when the SET of rows changes (not on every
	// relative-time tick of the sessions store).
	const itemsKey = $derived(items.map((i) => i.id).join('|'));

	let selected = $state(-1);
	const selectedItem = $derived(items[selected] ?? null);

	// Preview pane: on md+ a toggle ("Preview" in the footer); below md the pane
	// replaces the list after a tap and "← Back" returns to the list.
	let previewVisible = $state(true);
	let mobileDetail = $state(false);
	let actionsOpen = $state(false);

	const SEARCH_DEBOUNCE_MS = 150;

	// One transcript fetch per chat per dialog lifetime, shared by the preview
	// and the browser-side search. Promise-cached so concurrent askers dedupe.
	const transcriptCache = new Map<string, Promise<FeedMessage[]>>();
	function loadTranscript(id: string): Promise<FeedMessage[]> {
		let p = transcriptCache.get(id);
		if (!p) {
			p = loadHistory(id).then((entries) => foldHistory(entries).messages);
			p.catch(() => transcriptCache.delete(id)); // let a failed fetch retry later
			transcriptCache.set(id, p);
		}
		return p;
	}

	async function runLocal(t: string[], signal: AbortSignal): Promise<SessionSearchResult[]> {
		const out = await searchLocally(untrack(() => sessions), t, loadTranscript, signal);
		if (!signal.aborted) localSkipped = out.skipped;
		return out.results;
	}

	// Debounced, abortable search. Clearing the query cancels any in-flight
	// request and drops straight back to the recent-chats list.
	$effect(() => {
		const q = query;
		const t = queryTerms(q);
		if (t.length === 0) {
			results = null;
			searching = false;
			searchError = null;
			return;
		}
		searching = true;
		const ctrl = new AbortController();
		const timer = setTimeout(async () => {
			try {
				let res: SessionSearchResult[];
				if (untrack(() => localMode)) {
					res = await runLocal(t, ctrl.signal);
				} else {
					try {
						res = (await searchSessions(q, ctrl.signal)).results;
					} catch (err) {
						if (!(err instanceof SearchUnavailableError)) throw err;
						localMode = true;
						res = await runLocal(t, ctrl.signal);
					}
				}
				if (ctrl.signal.aborted) return;
				results = res;
				searchError = null;
			} catch (err: unknown) {
				if (ctrl.signal.aborted) return;
				results = [];
				searchError = err instanceof Error ? err.message : 'Search failed';
			} finally {
				if (!ctrl.signal.aborted) searching = false;
			}
		}, SEARCH_DEBOUNCE_MS);
		return () => {
			clearTimeout(timer);
			ctrl.abort();
		};
	});

	$effect(() => {
		void itemsKey;
		selected = items.length > 0 ? 0 : -1;
	});

	// Keep the selected row visible while arrowing through a long list.
	$effect(() => {
		const idx = selected;
		void tick().then(() => {
			listEl?.querySelector<HTMLElement>(`[data-index="${idx}"]`)?.scrollIntoView({ block: 'nearest' });
		});
	});

	// ── Preview pane ──────────────────────────────────────────────────────────
	let previewMessages = $state.raw<FeedMessage[] | null>(null);
	let previewLoading = $state(false);
	let previewError = $state<string | null>(null);

	$effect(() => {
		const id = selectedItem?.id ?? null;
		if (!id) {
			previewMessages = null;
			previewLoading = false;
			previewError = null;
			return;
		}
		previewMessages = null;
		previewError = null;
		previewLoading = true;
		let cancelled = false;
		loadTranscript(id)
			.then((msgs) => {
				if (cancelled) return;
				previewMessages = msgs;
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				previewError = err instanceof Error ? err.message : 'Could not load this chat';
			})
			.finally(() => {
				if (!cancelled) previewLoading = false;
			});
		return () => {
			cancelled = true;
		};
	});

	const rows = $derived(previewMessages ? previewRows(previewMessages, terms) : []);
	const firstHit = $derived(firstHitIndex(rows));

	// Scroll the preview to the first matching row (or the top for a
	// title-only match) once it has rendered.
	$effect(() => {
		const idx = firstHit;
		void rows;
		void tick().then(() => {
			if (!previewEl) return;
			const target = idx >= 0 ? previewEl.querySelector<HTMLElement>(`[data-row="${idx}"]`) : null;
			if (target) target.scrollIntoView({ block: 'center' });
			else previewEl.scrollTo({ top: 0 });
		});
	});

	// ── Open / close ──────────────────────────────────────────────────────────
	$effect(() => {
		if (open) {
			void tick().then(() => inputEl?.focus());
		} else {
			query = '';
			selected = -1;
			scope = 'all';
			mobileDetail = false;
			actionsOpen = false;
			localSkipped = 0;
			transcriptCache.clear();
		}
	});

	function chatPath(id: string): string {
		return '/s/' + id;
	}

	async function openChat(id: string | undefined): Promise<void> {
		if (!id) return;
		open = false;
		sidebar.setOpenMobile(false);
		await goto(chatPath(id));
	}

	function openInNewTab(id: string | undefined): void {
		if (!id) return;
		window.open(chatPath(id), '_blank', 'noopener');
	}

	async function copyLink(id: string | undefined): Promise<void> {
		if (!id) return;
		try {
			await navigator.clipboard.writeText(location.origin + chatPath(id));
			toast.success('Link copied');
		} catch {
			toast.error('Could not copy the link');
		}
	}

	function isTwoPane(): boolean {
		return typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
	}

	function onRowClick(index: number): void {
		selected = index;
		if (!isTwoPane()) mobileDetail = true;
	}

	function onBack(): void {
		if (isTwoPane()) selected = -1;
		else mobileDetail = false;
	}

	function onPreviewToggle(): void {
		if (isTwoPane()) previewVisible = !previewVisible;
		else mobileDetail = !mobileDetail;
	}

	// ↑/↓/↵ work from the query box and from a focused row alike.
	function onNavKeydown(e: KeyboardEvent): void {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			selected = stepSelection(selected, 1, items.length);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			selected = stepSelection(selected, -1, items.length);
		} else if (e.key === 'Enter') {
			e.preventDefault();
			void openChat(selectedItem?.id);
		}
	}

	// ⌘K while the dialog is open toggles the Actions menu for the selected chat
	// (the footer's "Actions ⌘K"). Registered in the CAPTURE phase on window so
	// it runs before — and stops — the layout's bubble-phase ⌘K listener, which
	// would otherwise close the dialog at the same time.
	$effect(() => {
		if (!open || typeof window === 'undefined') return;
		const onKey = (e: KeyboardEvent): void => {
			const mod = e.metaKey || e.ctrlKey;
			if (!mod || e.key.toLowerCase() !== 'k' || e.shiftKey || e.altKey) return;
			e.preventDefault();
			e.stopPropagation();
			if (selectedItem) actionsOpen = !actionsOpen;
		};
		window.addEventListener('keydown', onKey, true);
		return () => window.removeEventListener('keydown', onKey, true);
	});

	function formatWhen(ts: number): string {
		if (!ts) return '';
		return new Date(ts).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
	}

	const isMac = $derived(
		typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? ''),
	);
	const modKey = $derived(isMac ? '⌘' : 'Ctrl+');
</script>

{#snippet highlighted(text: string)}
	{#each highlightSegments(text, terms) as seg, i (i)}
		{#if seg.hit}<mark class="rounded-[2px] bg-yellow-300 px-px text-black">{seg.text}</mark>{:else}{seg.text}{/if}
	{/each}
{/snippet}

<Dialog.Root bind:open>
	<Dialog.Content
		showCloseButton={false}
		class="flex h-[min(52rem,92svh)] w-[calc(100%-1.5rem)] max-w-[calc(100%-1.5rem)] flex-col gap-0 overflow-hidden rounded-2xl border-0 bg-white p-0 text-[0.9375rem] text-gray-900 shadow-2xl sm:max-w-6xl dark:bg-[#2b2b2b] dark:text-gray-100"
		data-testid="chat-search-dialog"
	>
		<Dialog.Title class="sr-only">Search chats</Dialog.Title>
		<Dialog.Description class="sr-only">
			Search the titles and messages of your chats. Use the arrow keys to move through the results and Enter to open a chat.
		</Dialog.Description>

		<!-- Query bar -->
		<div class="flex h-[3.75rem] shrink-0 items-center gap-3 px-5">
			{#if searching}
				<Spinner class="size-5 shrink-0 text-gray-400" />
			{:else}
				<SearchIcon class="size-5 shrink-0 text-gray-500 dark:text-gray-400" strokeWidth={2} aria-hidden="true" />
			{/if}
			<input
				bind:this={inputEl}
				bind:value={query}
				type="text"
				autocomplete="off"
				autocorrect="off"
				spellcheck="false"
				maxlength="200"
				placeholder="Search"
				aria-label="Search chats"
				aria-controls="chat-search-results"
				aria-activedescendant={selectedItem ? `chat-search-row-${selectedItem.id}` : undefined}
				onkeydown={onNavKeydown}
				class="min-w-0 flex-1 bg-transparent text-[1.0625rem] font-medium outline-hidden placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
			/>
			{#if query}
				<button
					type="button"
					aria-label="Clear search"
					onclick={() => {
						query = '';
						inputEl?.focus();
					}}
					class="flex size-7 shrink-0 items-center justify-center rounded-md text-gray-500 transition hover:bg-black/5 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
				>
					<XIcon class="size-4" strokeWidth={2} aria-hidden="true" />
				</button>
			{/if}
			<DropdownMenu.Root>
				<DropdownMenu.Trigger
					class="flex h-7 shrink-0 items-center gap-1 rounded-md px-1.5 text-[0.9375rem] text-gray-600 transition hover:bg-black/5 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
					aria-label="Search scope: {SCOPE_LABEL[scope]}"
					title="Search scope"
				>
					{SCOPE_LABEL[scope]}
					<ChevronDownIcon class="size-3.5 opacity-70" strokeWidth={2} aria-hidden="true" />
				</DropdownMenu.Trigger>
				<DropdownMenu.Content align="end" sideOffset={6} class="min-w-[9rem]">
					{#each ['all', 'titles', 'messages'] as const as s (s)}
						<DropdownMenu.Item onSelect={() => (scope = s)} class={scope === s ? 'font-medium' : ''}>
							{SCOPE_LABEL[s]}
						</DropdownMenu.Item>
					{/each}
				</DropdownMenu.Content>
			</DropdownMenu.Root>
			<button
				type="button"
				aria-label="Close search"
				onclick={() => (open = false)}
				class="flex size-7 shrink-0 items-center justify-center rounded-md text-gray-500 transition hover:bg-black/5 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
			>
				<XIcon class="size-[1.125rem]" strokeWidth={2} aria-hidden="true" />
			</button>
		</div>
		<div class="h-px shrink-0 bg-gray-200 dark:bg-white/10" aria-hidden="true"></div>

		<div class="grid min-h-0 flex-1 {previewVisible ? 'md:grid-cols-[minmax(0,11fr)_minmax(0,9fr)]' : ''}">
			<!-- Results -->
			<div class="{mobileDetail ? 'hidden' : 'flex'} min-h-0 flex-col md:flex {previewVisible ? 'md:border-r md:border-gray-200 md:dark:border-white/10' : ''}">
				<div class="flex shrink-0 items-center justify-between px-5 pt-4 pb-2 text-[0.9375rem] text-gray-600 dark:text-gray-300">
					<span>{terms.length === 0 ? 'Recent' : 'Results'}</span>
					<span class="tabular-nums">{searching && results === null ? '…' : items.length}</span>
				</div>
				<div
					bind:this={listEl}
					id="chat-search-results"
					role="listbox"
					aria-label={terms.length === 0 ? 'Recent chats' : 'Matching chats'}
					class="scrollbar-hidden min-h-0 flex-1 overflow-y-auto px-2 pb-3"
				>
					{#if searchError}
						<p class="px-3 py-3 text-sm text-red-600 dark:text-red-400" role="alert">{searchError}</p>
					{:else if items.length === 0}
						<p class="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
							{#if terms.length === 0}
								No chats yet
							{:else if searching}
								Searching…
							{:else}
								No chats match “{query.trim()}”
							{/if}
						</p>
					{:else}
						{#each items as item, index (item.id)}
							<button
								type="button"
								id="chat-search-row-{item.id}"
								role="option"
								aria-selected={index === selected}
								data-index={index}
								data-session-id={item.id}
								onclick={() => onRowClick(index)}
								ondblclick={() => void openChat(item.id)}
								onkeydown={onNavKeydown}
								class="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left outline-hidden transition {index === selected
									? 'bg-black/[0.06] dark:bg-white/[0.07]'
									: 'hover:bg-black/[0.035] dark:hover:bg-white/[0.04]'}"
							>
								<MessageCircleIcon class="mt-[0.3rem] size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400" strokeWidth={1.75} aria-hidden="true" />
								<span class="flex min-w-0 flex-1 flex-col gap-0.5">
									<span class="flex items-baseline gap-3">
										<span class="min-w-0 flex-1 truncate text-[1.0625rem] font-medium text-gray-900 dark:text-gray-100">
											{@render highlighted(item.title)}
										</span>
										<span class="shrink-0 text-[0.9375rem] text-gray-500 dark:text-gray-400">Personal chat</span>
									</span>
									{#if item.snippet}
										<span class="truncate text-[0.9375rem] text-gray-600 dark:text-gray-300">{@render highlighted(item.snippet.text)}</span>
									{:else if terms.length > 0 && item.titleMatch}
										<span class="text-[0.9375rem] text-gray-500 dark:text-gray-400">Matches the title</span>
									{:else if terms.length === 0}
										<span class="text-[0.9375rem] text-gray-500 dark:text-gray-400">{formatWhen(item.lastActivityAt)}</span>
									{/if}
								</span>
							</button>
						{/each}
					{/if}
					{#if localMode && terms.length > 0 && !searching && localSkipped > 0}
						<p class="px-3 pt-3 text-xs text-gray-500 dark:text-gray-400">
							Searched your most recent chats; {localSkipped} older {localSkipped === 1 ? 'chat was' : 'chats were'} not searched.
						</p>
					{/if}
				</div>
			</div>

			<!-- Preview -->
			<div class="{mobileDetail ? 'flex' : 'hidden'} min-h-0 flex-col {previewVisible ? 'md:flex' : 'md:hidden'}">
				<div class="flex shrink-0 items-center justify-between px-5 pt-4 pb-2 text-[0.9375rem] text-gray-600 dark:text-gray-300">
					<button
						type="button"
						onclick={onBack}
						class="flex items-center gap-1.5 rounded-md py-0.5 pr-1.5 -ml-0.5 transition hover:text-gray-900 dark:hover:text-white"
					>
						<ArrowLeftIcon class="size-4" strokeWidth={2} aria-hidden="true" />
						Back
					</button>
					<span>Chats</span>
				</div>
				{#if selectedItem}
					<div bind:this={previewEl} class="scrollbar-hidden min-h-0 flex-1 overflow-y-auto px-5 pb-5" data-testid="chat-search-preview">
						<h2 class="mb-4 text-[1.0625rem] font-medium text-gray-900 dark:text-gray-100">{selectedItem.title}</h2>
						{#if previewLoading}
							<div class="flex items-center gap-2 py-6 text-sm text-gray-500">
								<Spinner class="size-4" />
								Loading chat…
							</div>
						{:else if previewError}
							<p class="py-6 text-sm text-red-600 dark:text-red-400" role="alert">{previewError}</p>
						{:else if rows.length === 0}
							<p class="py-6 text-sm text-gray-500 dark:text-gray-400">Nothing has been said in this chat yet</p>
						{:else}
							<ol class="flex flex-col gap-3">
								{#each rows as row, i (i)}
									{#if row.role === 'user'}
										<li data-row={i} data-hit={row.hit ? '' : undefined} class="flex flex-col items-end gap-1.5">
											<div class="max-w-[90%] rounded-3xl bg-gray-100 px-4 py-2 leading-relaxed whitespace-pre-wrap break-words text-gray-900 dark:bg-white/[0.09] dark:text-gray-100">
												{@render highlighted(row.text)}
											</div>
											{#if row.timestamp}<span class="pr-1 text-[0.9375rem] text-gray-500 dark:text-gray-400">{formatWhen(row.timestamp)}</span>{/if}
										</li>
									{:else}
										<li data-row={i} data-hit={row.hit ? '' : undefined} class="flex flex-col items-start gap-1.5">
											<div class="px-1 leading-relaxed whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100">
												{@render highlighted(row.text)}
											</div>
											{#if row.timestamp}<span class="pl-2 text-[0.9375rem] text-gray-500 dark:text-gray-400">{formatWhen(row.timestamp)}</span>{/if}
										</li>
									{/if}
								{/each}
							</ol>
						{/if}
					</div>
				{:else}
					<div class="flex flex-1 items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
						{terms.length === 0 ? 'Pick a chat to preview it' : 'Nothing to preview'}
					</div>
				{/if}
			</div>
		</div>

		<div class="h-px shrink-0 bg-gray-200 dark:bg-white/10" aria-hidden="true"></div>
		<!-- Footer -->
		<div class="flex h-12 shrink-0 items-center justify-between gap-4 px-5 text-[0.9375rem] text-gray-600 dark:text-gray-300">
			<span class="truncate">{selectedItem ? 'Personal chat' : ''}</span>
			<div class="flex shrink-0 items-center gap-5">
				<button
					type="button"
					disabled={!selectedItem}
					onclick={() => void openChat(selectedItem?.id)}
					class="flex items-center gap-1.5 rounded-md transition hover:text-gray-900 disabled:opacity-40 dark:hover:text-white"
				>
					Open
					<CornerDownLeftIcon class="size-4" strokeWidth={2} aria-hidden="true" />
				</button>
				<button
					type="button"
					onclick={onPreviewToggle}
					aria-pressed={isTwoPane() ? previewVisible : mobileDetail}
					class="flex items-center gap-1.5 rounded-md transition hover:text-gray-900 dark:hover:text-white"
				>
					<EyeIcon class="size-4" strokeWidth={2} aria-hidden="true" />
					Preview
				</button>
				<DropdownMenu.Root bind:open={actionsOpen}>
					<DropdownMenu.Trigger
						disabled={!selectedItem}
						class="flex items-center gap-1.5 rounded-md transition hover:text-gray-900 disabled:opacity-40 dark:hover:text-white"
					>
						Actions <span class="text-gray-400 dark:text-gray-500">{modKey}K</span>
					</DropdownMenu.Trigger>
					<DropdownMenu.Content align="end" side="top" sideOffset={8} class="min-w-[12.5rem]">
						<DropdownMenu.Item onSelect={() => void openChat(selectedItem?.id)}>
							<CornerDownLeftIcon strokeWidth={1.5} aria-hidden="true" />
							Open chat
						</DropdownMenu.Item>
						<DropdownMenu.Item onSelect={() => openInNewTab(selectedItem?.id)}>
							<ExternalLinkIcon strokeWidth={1.5} aria-hidden="true" />
							Open in new tab
						</DropdownMenu.Item>
						<DropdownMenu.Item onSelect={() => void copyLink(selectedItem?.id)}>
							<LinkIcon strokeWidth={1.5} aria-hidden="true" />
							Copy link
						</DropdownMenu.Item>
					</DropdownMenu.Content>
				</DropdownMenu.Root>
			</div>
		</div>
	</Dialog.Content>
</Dialog.Root>
